import { useState, useCallback, useMemo } from 'react';
import { trpc } from 'src/trpc';
import { useToast } from 'src/components/useToast';
import { useOrganization } from 'src/context/OrganizationContext';
import { CityFee } from '@libs/schemas';

interface UseCityFeesOptions {
  includeArchived?: boolean;
  search?: string;
}

interface OptimisticCityFee extends CityFee {
  _isOptimistic?: boolean;
  _tempId?: string;
  _pendingOperation?: 'create' | 'update' | 'archive' | 'unarchive';
}

export function useCityFees(options: UseCityFeesOptions = {}) {
  const { organizationId, isResolved } = useOrganization();
  const { successToast, errorToast } = useToast();
  const utils = trpc.useUtils();

  // Local state for optimistic updates
  const [optimisticFees, setOptimisticFees] = useState<OptimisticCityFee[]>([]);

  // Main query for city fees
  const cityFeesQuery = trpc.admin.cityFee.list.useQuery(
    {
      includeArchived: options.includeArchived ?? false,
      search: options.search,
    },
    {
      enabled: isResolved && !!organizationId,
      staleTime: 10000, // 10 seconds
      refetchOnWindowFocus: false,
    },
  );

  // Stats query
  const statsQuery = trpc.admin.cityFee.stats.useQuery(undefined, {
    enabled: isResolved && !!organizationId,
    staleTime: 10000, // 10 seconds
  });

  // Create mutation
  const createMutation = trpc.admin.cityFee.create.useMutation({
    onMutate: async (newFee) => {
      await utils.admin.cityFee.list.cancel();

      const tempId = `temp-${Date.now()}`;
      const optimisticFee: OptimisticCityFee = {
        _id: tempId,
        ...newFee,
        archived: newFee.archived ?? false,
        organizationId: organizationId || '',
        createdAt: new Date(),
        updatedAt: new Date(),
        _isOptimistic: true,
        _tempId: tempId,
        _pendingOperation: 'create',
      };

      setOptimisticFees((prev) => [optimisticFee, ...prev]);
      return { optimisticFee };
    },
    onError: (error, _variables, context) => {
      if (context?.optimisticFee._tempId) {
        setOptimisticFees((prev) =>
          prev.filter((f) => f._tempId !== context.optimisticFee._tempId),
        );
      }
      errorToast({
        title: 'Failed to create city fee',
        description: error.message,
      });
    },
    onSuccess: (result, _variables, context) => {
      if (context?.optimisticFee._tempId) {
        setOptimisticFees((prev) =>
          prev.filter((f) => f._tempId !== context.optimisticFee._tempId),
        );
      }
      utils.admin.cityFee.list.invalidate();
      utils.admin.cityFee.stats.invalidate();
      successToast({
        title: 'City fee created',
        description: `Successfully created fee for ${result.city}, ${result.state}`,
      });
    },
  });

  // Update mutation
  const updateMutation = trpc.admin.cityFee.update.useMutation({
    onMutate: async (updatedFee) => {
      await utils.admin.cityFee.list.cancel();

      setOptimisticFees((prev) => {
        const index = prev.findIndex((f) => f._id === updatedFee._id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            ...updatedFee,
            _pendingOperation: 'update',
          };
          return updated;
        }
        return prev;
      });

      return { originalFee: updatedFee };
    },
    onError: (error) => {
      utils.admin.cityFee.list.invalidate();
      errorToast({
        title: 'Failed to update city fee',
        description: error.message,
      });
    },
    onSuccess: (result, variables) => {
      setOptimisticFees((prev) => prev.filter((f) => f._id !== variables._id));
      utils.admin.cityFee.list.invalidate();
      utils.admin.cityFee.stats.invalidate();
      successToast({
        title: 'City fee updated',
        description: `Successfully updated fee for ${result.city}, ${result.state}`,
      });
    },
  });

  // Archive mutation
  const archiveMutation = trpc.admin.cityFee.archive.useMutation({
    onMutate: async ({ _id }) => {
      await utils.admin.cityFee.list.cancel();

      setOptimisticFees((prev) =>
        prev.map((fee) =>
          fee._id === _id ? { ...fee, _pendingOperation: 'archive' as const } : fee,
        ),
      );

      return { _id };
    },
    onError: (error, _variables, context) => {
      setOptimisticFees((prev) =>
        prev.map((fee) =>
          fee._id === context?._id ? { ...fee, _pendingOperation: undefined } : fee,
        ),
      );
      errorToast({
        title: 'Failed to archive city fee',
        description: error.message,
      });
    },
    onSuccess: (_result, variables) => {
      setOptimisticFees((prev) => prev.filter((f) => f._id !== variables._id));
      utils.admin.cityFee.list.invalidate();
      utils.admin.cityFee.stats.invalidate();
      successToast({
        title: 'City fee archived',
        description: 'Successfully archived city fee',
      });
    },
  });

  // Unarchive mutation
  const unarchiveMutation = trpc.admin.cityFee.unarchive.useMutation({
    onMutate: async ({ _id }) => {
      await utils.admin.cityFee.list.cancel();

      setOptimisticFees((prev) =>
        prev.map((fee) =>
          fee._id === _id ? { ...fee, _pendingOperation: 'unarchive' as const } : fee,
        ),
      );

      return { _id };
    },
    onError: (error, _variables, context) => {
      setOptimisticFees((prev) =>
        prev.map((fee) =>
          fee._id === context?._id ? { ...fee, _pendingOperation: undefined } : fee,
        ),
      );
      errorToast({
        title: 'Failed to unarchive city fee',
        description: error.message,
      });
    },
    onSuccess: (_result, variables) => {
      setOptimisticFees((prev) => prev.filter((f) => f._id !== variables._id));
      utils.admin.cityFee.list.invalidate();
      utils.admin.cityFee.stats.invalidate();
      successToast({
        title: 'City fee unarchived',
        description: 'Successfully unarchived city fee',
      });
    },
  });

  // Merge server data with optimistic updates
  const mergedFees = useMemo(() => {
    const serverFees = cityFeesQuery.data || [];

    const validOptimisticFees = optimisticFees.filter(
      (fee) => fee._pendingOperation !== 'archive' && fee._pendingOperation !== 'unarchive',
    );

    const optimisticCreates = validOptimisticFees.filter(
      (fee) => fee._pendingOperation === 'create',
    );

    const updatedServerFees = serverFees.map((serverFee) => {
      const optimisticUpdate = validOptimisticFees.find(
        (opt) => opt._id === serverFee._id && opt._pendingOperation === 'update',
      );
      return optimisticUpdate || serverFee;
    });

    return [...optimisticCreates, ...updatedServerFees] as CityFee[];
  }, [cityFeesQuery.data, optimisticFees]);

  // Helper functions
  const createFee = useCallback(
    (data: { city: string; state: string; feeInCents: number; archived?: boolean }) => {
      createMutation.mutate(data);
    },
    [createMutation],
  );

  const updateFee = useCallback(
    (
      _id: string,
      data: { city?: string; state?: string; feeInCents?: number; archived?: boolean },
    ) => {
      updateMutation.mutate({ _id, ...data });
    },
    [updateMutation],
  );

  const archiveFee = useCallback(
    (_id: string) => {
      archiveMutation.mutate({ _id });
    },
    [archiveMutation],
  );

  const unarchiveFee = useCallback(
    (_id: string) => {
      unarchiveMutation.mutate({ _id });
    },
    [unarchiveMutation],
  );

  return {
    // Data
    cityFees: mergedFees,
    stats: statsQuery.data,
    isLoading: cityFeesQuery.isLoading,
    isLoadingStats: statsQuery.isLoading,

    // Mutations
    createFee,
    updateFee,
    archiveFee,
    unarchiveFee,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isArchiving: archiveMutation.isPending,
    isUnarchiving: unarchiveMutation.isPending,

    // Error state
    error: cityFeesQuery.error,

    // Manual refetch
    refetch: cityFeesQuery.refetch,
  };
}
